import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";

import AddAssetsToPackModal from "components/modal/AddAssetsToPacksModal/AddAssetsToPackModal";
import { useLocation, useNavigate } from "react-router";
import { WizardNew as Wizard } from "../../../../components/wizard";
import Section from "../../../../components/wizard/Section";
import { PublishedStatusTypes, RouteNames } from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import { bindAction } from "../../../../interfaces";
import navigationUtils from "../../../../utils/navigationUtils";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import { ScormPublishSuccess } from "../../../Application/services/realTimeNotification/events/library/libraryEvents";
import Configure from "../Details/Configure";
import SettingsTab from "../Details/SettingsTab";
import { reset as resetDetails } from "../state/slices/DetailsSlice";
import { resetTags } from "../state/slices/TagsSlice";
import { publishItemAction } from "../state/thunks/EntityStateThunk";
import { noop } from "lodash";

enum CreateSteps {
  Configuration,
  Settings,
}
const pageIndexes = [CreateSteps.Configuration, CreateSteps.Settings];
const overviewUrl = `/${RouteNames.contentScorm}`;
const showLastModified = new Set([CreateSteps.Configuration]);

export type Props = PropsFromRedux;

export const Create = (props: Props) => {
  const { publishItem, entityId, resetTags, onUnmount } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const [isValid, setIsValid] = useState(false);
  const [showAddPacks, setShowAddPacks] = useState(false);
  const selectedItemIds = useMemo(() => [entityId], [entityId]);

  const [wizardPages, { onNext, onPrevious, onFinish }, subscribeOnActiveIndexChanged] =
    useWizardStepsManager(pageIndexes);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    return () => {
      resetTags();
      onUnmount();
    };
  }, [onUnmount, resetTags]);

  useEffect(() => {
    const handler = (_: CreateSteps, activeStepIndex: CreateSteps) => {
      setActiveTab(activeStepIndex);
    };

    return subscribeOnActiveIndexChanged(handler);
  }, [subscribeOnActiveIndexChanged]);

  const onPublished = () => setShowAddPacks(true);
  useRtn([ScormPublishSuccess], onPublished);

  const handleCancelClick = useCallback(() => {
    navigationUtils.goBackOrDefault(location, navigate, overviewUrl);
  }, [location, navigate]);

  const handleOnFinish = useCallback(() => {
    onFinish?.();
    publishItem(entityId);
  }, [onFinish, publishItem, entityId]);

  const getPublishedStatus = () => (props.isEntityCreated ? PublishedStatusTypes.notPublished : null);

  const onPreviousAsync = async (activeStepIndex: number, nextIndex: number) => {
    onPrevious(activeStepIndex, nextIndex);
  };

  return (
    <>
      <Wizard
        title="Import SCORM Package"
        finishButtonLabel="Finish"
        onCancel={handleCancelClick}
        onProgress={onNext}
        onRegressAsync={onPreviousAsync}
        onFinish={handleOnFinish}
        publishedStatus={getPublishedStatus()}
        isSaveInProgress={props.isSaving}
        isFinishButtonDisabled={!props.canBePublished}
        finishTooltipMessage={
          !props.canBePublished ? "SCORM is still processing and can be published when finished." : undefined
        }
        progressSavedDate={showLastModified.has(activeTab) ? props.dateModified : undefined}
      >
        <Section label="Configuration" className="scrollable-content" isLocked={!isValid} required>
          <Configure
            acceptHandlers={wizardPages[CreateSteps.Configuration]}
            onIsValidChange={setIsValid}
            mode="create"
          />
        </Section>
        <Section label="Settings" className="scrollable-content">
          <SettingsTab acceptHandlers={wizardPages[CreateSteps.Settings]} />
        </Section>
      </Wizard>
      <AddAssetsToPackModal
        showModal={showAddPacks}
        onClose={() => navigate(overviewUrl)}
        onComplete={noop}
        selectedItemIds={selectedItemIds}
        contentType="Scorm"
      />
    </>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const entityState = state.library.scorm.entityState;
  const details = state.library.scorm.details;
  return {
    entityId: entityState.entityId,
    isEntityCreated: entityState.entityId > 0,
    isSaving: entityState.isEntityCommandInProgress,
    dateModified: entityState.lastModifiedDateTime,
    canBePublished: details.item.canBePublished,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  publishItem: bindAction(publishItemAction, dispatch),
  onUnmount: bindAction(resetDetails, dispatch),
  resetTags: bindAction(resetTags, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Create);
export default ConnectedComponent;
